import { toSnakeCase } from "@repo/mfe-core";
import { useDebounce } from "@repo/mfe-core/host";
import { MenuOption } from "@saviynt/design-system";
import { useCallback, useMemo, useState } from "react";

export function useFindingsCommon() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filterOptions, setFilterOptions] = useState<
    Record<string, MenuOption[]>
  >({});
  const debouncedSearch = useDebounce(searchValue.trim(), 500);
  const debouncedFilters = useDebounce(filterOptions, 500);

  const hasSelectedFilters = useMemo(
    () =>
      Object.values(debouncedFilters).some((options) =>
        options.some((option) => option.isChecked),
      ),
    [debouncedFilters],
  );

  const handleFilterToggle = useCallback(() => {
    setIsFilterOpen((prev) => !prev);
  }, []);

  const handleClear = useCallback(() => {
    setFilterOptions((prev) => {
      const clearedOptions = { ...prev };
      Object.keys(clearedOptions).forEach((key) => {
        clearedOptions[key] = clearedOptions[key].map((option) => ({
          ...option,
          isChecked: false,
        }));
      });
      return clearedOptions;
    });
  }, []);

  return {
    isFilterOpen,
    searchValue,
    filterOptions,
    debouncedSearch,
    debouncedFilters,
    hasSelectedFilters,
    handleFilterToggle,
    handleClear,
    setSearchValue,
    setFilterOptions,
  };
}

export function useFindingsPayload(
  debouncedSearch?: string,
  debouncedFilters?: Record<string, MenuOption[]>,
) {
  return useMemo(() => {
    const payload: Record<string, string | string[]> = {};

    if (debouncedSearch) {
      payload.display_name = debouncedSearch;
    }

    Object.entries(debouncedFilters || {}).forEach(([filterName, options]) => {
      const selectedValues = options
        .filter((option) => option.isChecked)
        .map((option) => option.value as string);
      if (selectedValues.length > 0) {
        payload[toSnakeCase(filterName)] = selectedValues;
      }
    });

    return payload;
  }, [debouncedSearch, debouncedFilters]);
}
